import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideCircleCheck,
  lucideLogOut,
  lucideRotateCw,
  lucideTriangleAlert,
  lucideUsers,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmAlertDialogImports } from '@spartan-ng/helm/alert-dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import { LogoComponent } from '../../components/logo/logo.component';
import { AuthService } from '../../services/auth.service';
import { PartnerClient, PartnerOverview, PartnerService } from '../../services/partner.service';
import { AddClientMenuComponent } from './components/add-client-menu/add-client-menu.component';
import { ClaimMailDialogComponent } from './components/claim-mail-dialog/claim-mail-dialog.component';
import { CreateClientDialogComponent } from './components/create-client-dialog/create-client-dialog.component';
import { RequestAccessDialogComponent } from './components/request-access-dialog/request-access-dialog.component';
import { PartnerClientListComponent } from './components/partner-client-list/partner-client-list.component';
import { PartnerLinkCardComponent } from './components/partner-link-card/partner-link-card.component';
import { PartnerProfileStepComponent } from './components/partner-profile-step/partner-profile-step.component';

/**
 * The partner area (/partner/clients): the register link and the shops the partner
 * brought in or works for.
 */
@Component({
  selector: 'app-partner-area',
  standalone: true,
  imports: [
    NgIcon,
    HlmAlertImports,
    HlmAlertDialogImports,
    HlmButtonImports,
    HlmCardImports,
    HlmSkeletonImports,
    HlmTypographyImports,
    LogoComponent,
    PartnerClientListComponent,
    PartnerLinkCardComponent,
    PartnerProfileStepComponent,
    CreateClientDialogComponent,
    ClaimMailDialogComponent,
    AddClientMenuComponent,
    RequestAccessDialogComponent,
  ],
  providers: [
    provideIcons({
      lucideCircleCheck,
      lucideLogOut,
      lucideRotateCw,
      lucideTriangleAlert,
      lucideUsers,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-area.component.html',
})
export class PartnerAreaComponent {
  protected readonly partnerService = inject(PartnerService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  readonly overview = signal<PartnerOverview | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  /** Confirmation after creating an account or mailing an invitation. */
  readonly notice = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);

  readonly createOpen = signal(false);
  readonly requestOpen = signal(false);
  /** The client whose access the agency is about to end (confirmation dialog). */
  readonly leaving = signal<PartnerClient | null>(null);
  readonly claimMailFor = signal<PartnerClient | null>(null);
  /** public_id of the account being opened. */
  readonly opening = signal<string | null>(null);

  constructor() {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.partnerService.overview().subscribe({
      next: (overview) => {
        this.overview.set(overview);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Deine Kunden konnten nicht geladen werden.');
        this.loading.set(false);
      },
    });
  }

  onCreated(client: PartnerClient): void {
    this.createOpen.set(false);
    this.notice.set(
      `Das Konto für ${client.name} ist angelegt. Du kannst es jetzt einrichten. Dein Kunde hat eine E-Mail bekommen, mit der er ein eigenes Passwort festlegt.`,
    );
    this.load();
  }

  onClaimMailSent(client: PartnerClient): void {
    this.claimMailFor.set(null);
    this.notice.set(`Der Anmelde-Link ist an ${client.email} unterwegs.`);
    this.load();
  }

  onAccessRequested(message: string): void {
    this.requestOpen.set(false);
    this.notice.set(message);
  }

  onLeaveDialogState(state: 'open' | 'closed'): void {
    if (state === 'closed') this.leaving.set(null);
  }

  confirmLeave(): void {
    const client = this.leaving();
    this.leaving.set(null);
    if (!client?.public_id) return;
    this.actionError.set(null);
    this.partnerService.leaveClient(client.public_id).subscribe({
      next: () => {
        this.notice.set(`Du betreust ${client.name || client.email} nicht mehr.`);
        this.load();
      },
      error: () => this.actionError.set('Der Zugriff konnte nicht beendet werden.'),
    });
  }

  /** Switches into the client's account; "Zurück zum Partnerbereich" leaves it again. */
  openClient(client: PartnerClient): void {
    if (!client.public_id || this.opening()) return;
    this.opening.set(client.public_id);
    this.actionError.set(null);
    this.partnerService.openClient(client.public_id).subscribe({
      next: (session) => {
        this.authService.startActingSession(session);
        this.opening.set(null);
        this.router.navigate(['/']);
      },
      error: () => {
        this.opening.set(null);
        this.actionError.set('Das Konto konnte nicht geöffnet werden. Bitte lade die Seite neu.');
      },
    });
  }

  logout(): void {
    this.partnerService.logout();
  }
}
